import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, inputBase, card, pageHeading } from "../theme";
import HelpButton from "../components/HelpButton";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";

export default function TablesPage() {
  const [tables, setTables] = useState([]);
  const [businessName, setBusinessName] = useState("");
  const [label, setLabel] = useState("");
  const [seats, setSeats] = useState(4);
  const [capMessage, setCapMessage] = useState("");
  const [maxTables, setMaxTables] = useState(null);

  const authHeader = () => getAuthHeader();

  const load = () => {
    if (!getAuthHeader().Authorization) {
      window.location.href = getLoginRedirect();
      return;
    }
    axios.get(endpoints.tables, { headers: authHeader() })
      .then((res) => setTables(res.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          window.location.href = getLoginRedirect();
        }
      });

    setBusinessName(getBusinessName());
    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    if (tenant) {
      setMaxTables(tenant.max_tables);
    }
  };

  useEffect(load, []);

  const addTable = async (e) => {
    e.preventDefault();
    setCapMessage("");
    try {
      await axios.post(endpoints.tables, { label, seats: parseInt(seats, 10) || 4 }, { headers: authHeader() });
      setLabel("");
      setSeats(4);
      load();
    } catch (err) {
      setCapMessage(err.response?.data?.message || "Could not add table.");
    }
  };

  const deleteTable = async (id, tableLabel) => {
    if (!window.confirm(`Delete "${tableLabel}"?`)) return;
    await axios.delete(endpoints.tableDelete(id), { headers: authHeader() });
    load();
  };

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 640 }}>
        <h1 style={pageHeading}>Tables <HelpButton page="tables" /></h1>

        <form className="inline-form" onSubmit={addTable} style={styles.inlineForm}>
          <input
            style={styles.input}
            placeholder="Table label (e.g. Table 1, Patio A)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={maxTables !== null && tables.length >= maxTables}
            required
          />
          <input
            style={{ ...styles.input, width: 80 }}
            type="number"
            min="1"
            placeholder="Seats"
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            disabled={maxTables !== null && tables.length >= maxTables}
          />
          <button
            style={{
              ...styles.addBtn,
              ...(maxTables !== null && tables.length >= maxTables ? styles.addBtnDisabled : {}),
            }}
            type="submit"
            disabled={maxTables !== null && tables.length >= maxTables}
          >
            Add Table
          </button>
        </form>
        {maxTables !== null && tables.length >= maxTables && (
          <div style={styles.capMessage}>
            Limit reached: max {maxTables} tables. Upgrade to add more.
          </div>
        )}
        {capMessage && <div style={styles.capMessage}>{capMessage}</div>}

        {tables.length === 0 ? (
          <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>No tables yet. Add one above to get started.</p>
        ) : (
          <div style={styles.grid}>
            {tables.map((t) => (
              <div key={t.id} style={styles.tableCard}>
                <div style={{ fontFamily: fonts.display, fontWeight: 600, color: colors.textPrimary }}>{t.label}</div>
                <div style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary }}>{t.seats} seats</div>
                <div style={{ fontFamily: fonts.body, fontSize: 12, color: t.is_active ? colors.statusReady : colors.textMuted }}>
                  {t.is_active ? "Active" : "Inactive"}
                </div>
                <button onClick={() => deleteTable(t.id, t.label)} style={styles.deleteLink}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  inlineForm: { display: "flex", gap: space[2], marginTop: space[3], marginBottom: space[4] },
  input: { ...inputBase, flex: 1, padding: "8px 10px" },
  addBtn: { ...buttonPrimary, padding: "8px 14px", whiteSpace: "nowrap" },
  addBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  capMessage: {
    background: "#fef3c7",
    color: "#92400e",
    padding: "8px 12px",
    borderRadius: radius.sm,
    fontSize: 13,
    fontFamily: fonts.body,
    marginBottom: 8,
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: space[3] },
  tableCard: { ...card, padding: 14 },
  deleteLink: {
    background: "none",
    border: "none",
    color: "#b91c1c",
    fontSize: 12,
    fontFamily: fonts.body,
    cursor: "pointer",
    padding: 0,
    marginTop: 8,
  },
};
